<template>
    <div class="bg-white">
        <div class="mb-3">
            <h1 class="h4 mb-0">Order Stationery</h1>
            <p>Request lab tickets or bags for your practice. Once your request has been processed, we’ll include them with your next delivery.</p>
            
        </div>
         
        <div class="row">
            <div class="col-md-6 mb-3">
                <DropdownInput
                    label="Account"
                    v-model="account_id"
                    :v="v$.account_id"
                    :options="accounts"
                />
            </div>
        </div>
        
        <div class="row">
            <div class="col-md-12 mb-3">
                <label>What ticket/stationery type do you need?</label>
                <div class="btn-helper-text mb-3">Select one or more item. Lab ticket types are pre-printed for each treatment category</div>
                <div :class="{ 'is-invalid': v$.selectedOptions.$error }">
                    <span
                        v-for="option in stationeryOptions"
                        :key="option.id"
                        :title="option.disabled ? 'You have a pending request for this item' : ''"
                        class="d-inline-block"
                    >
                        <button
                            type="button"
                            class="btn me-1 mb-1"
                            :class="{'btn-primary': selectedOptions.includes(option.id), 'btn-light': !selectedOptions.includes(option.id)}"
                            :disabled="option.disabled"
                            @click="toggleOption(option.id)"
                        >{{ option.label }}</button>
                    </span>
                </div>
                <span class="invalid-feedback" v-if="v$.selectedOptions.$error">{{ v$.selectedOptions.$errors[0].$message }}</span>
            </div>
        </div>
        <div class="buttons">
            <button class="btn btn-primary me-1" type="button" @click="showModal" :disabled="submitting">
                Submit
            </button>
            <router-link class="btn btn-light" :to="{ name: 'Stationery' }">Cancel</router-link>
        </div>

        <div class="reorder-notice mt-3 d-flex align-items-start">
            <i class="bi bi-clock me-2"></i>
            <div>Each item can only be reordered once every 7 days. Items requested within the last week are greyed out and can’t be selected, which helps prevent duplicate orders.</div>
        </div>
       

        <div class="modal fade" id="connect-modal" tabindex="-1" ref="modalRef">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Did you know?</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p>By submitting orders digitally via <strong>Connect</strong>, you can eliminate the need for physical stationery entirely.</p>
                        <ul>
                            <li>No more waiting for lab tickets, bags or labels to arrive</li>
                            <li>Reduce paper waste and go green</li>
                            <li>Track your orders in real-time</li>
                            <li>Faster turnaround on your cases</li>
                        </ul>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" @click="confirmSubmit" :disabled="submitting">
                            {{ submitting ? 'Submitting...' : 'Continue with Request' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { Modal } from 'bootstrap';
    import DropdownInput from '@/components/DropdownInput.vue';
    import api from '@/services/api';
    import useVuelidate from '@vuelidate/core';
    import { required, minLength } from '@vuelidate/validators';

    export default {
        setup() {
            return { v$: useVuelidate() }
        },
        components: {
            DropdownInput,
        },
        data() {
            return {
                account_id: '',
                accounts: [],
                selectedOptions: [],
                currentOrders: [],
                submitting: false,
                modalInstance: null,
                stationeryOptions: [
                    { id: 1, label: 'Crown & Bridge Tickets', disabled: false },
                    { id: 2, label: 'Implant Restoration Tickets', disabled: false },
                    { id: 4, label: 'Orthodontic Appliance Tickets', disabled: false },
                    { id: 8, label: 'Denture & Prosthetic Tickets', disabled: false },
                    { id: 16, label: 'Plastic Bags', disabled: false },
                ],
            }
        },
        validations() {
            return {
                account_id: { required },
                selectedOptions: { required, minLength: minLength(1) },
            }
        },
        created() {
            this.fetchAccounts();
        },
        watch: {
            account_id() {
                this.disableOptionsForCurrentOrders();
            },
        },
        mounted() {
            this.modalInstance = new Modal(this.$refs.modalRef, {});
            this.fetchCurrentOrders();
        },
        methods: {
            fullName(account) {
                return account.dentist.first_name + ' ' + account.dentist.surname + ' (' + account.code + ')';
            },
            fetchAccounts() {
                api
                .get('/api/v1/accounts', {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                        bookingOnCheck: true,
                    }
                })
                .then((response) => {
                    this.accounts = response.data.data.map((account) => { return {
                        id: account.id,
                        label: this.fullName(account),
                        dentist_id: account.dentist && account.dentist.id,
                    }});
                })
                .catch(() => {
                    console.log('Catch accounts error');
                });
            },
            fetchCurrentOrders() {
                api
                .get('/api/v1/stationery/recent-orders', {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                    }
                })
                .then((response) => {
                    this.currentOrders = response.data;
                    this.disableOptionsForCurrentOrders();
                })
                .catch((error) => {
                    console.log('Catch current orders error:', error);
                });
            },
            toggleOption(id) {
                const index = this.selectedOptions.indexOf(id);
                if (index > -1) {
                    this.selectedOptions.splice(index, 1);
                } else {
                    this.selectedOptions.push(id);
                }
            },
            async showModal() {
                const isValid = await this.v$.$validate();
                if (!isValid) return;

                this.modalInstance.show();
            },
            confirmSubmit() {
                this.submitting = true;
                api
                .post('/api/v1/stationery/create-order', {
                    account_id: this.account_id,
                    options: this.selectedOptions,
                })
                .then(() => {
                    this.modalInstance.hide();
                    this.$router.push({ name: 'Stationery' });
                })
                .catch((error) => {
                    alert(error.response?.data?.message || 'An error occurred. Please try again.');
                })
                .finally(() => {
                    this.submitting = false;
                });
            },
            disableOptionsForCurrentOrders() {
                this.stationeryOptions.forEach((option) => { option.disabled = false; });

                if (!this.account_id) return;
                const selectedAccount = this.accounts.find(a => a.id == this.account_id);
                if (!selectedAccount) return;

                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

                this.currentOrders.forEach((order) => {
                    if (new Date(order.created_at) < sevenDaysAgo || order.completed_at) return;
                    if (!order.dentist || order.dentist.id !== selectedAccount.dentist_id) return;

                    order.items.forEach((item) => {
                        const option = this.stationeryOptions.find(o => o.id === item.type);
                        if (option) {
                            option.disabled = true;
                        }
                    });
                });

                this.selectedOptions = this.selectedOptions.filter((id) => {
                    const option = this.stationeryOptions.find(o => o.id === id);
                    return option && !option.disabled;
                });
            },
        },
    }
</script>

<style scoped>
    button:disabled {
        pointer-events: none;
    }
    span:has(button:disabled) {
        cursor: not-allowed;
    }
    .helper-text {
        font-size: 0.7em;
        color: #666666;
        line-height: 1;
        margin-top: -0.15em;
    }
    .btn-helper-text {
        font-size: 0.9em;
    }
    .reorder-notice {
        background-color: #dbe6f7;
        border-radius: 0.5rem;
        padding: 0.75rem 1rem;
        color: #3a4a63;
        font-size: 0.9em;
    }
    .reorder-notice i {
        font-size: 1.1rem;
        line-height: 1.4;
    }
</style>
